// src/events.ts
function createEmitter() {
  const listeners = /* @__PURE__ */ new Map();
  function getSet(event) {
    let set = listeners.get(event);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      listeners.set(event, set);
    }
    return set;
  }
  return {
    on(event, listener) {
      getSet(event).add(listener);
      return () => getSet(event).delete(listener);
    },
    off(event, listener) {
      getSet(event).delete(listener);
    },
    emit(event, payload) {
      const set = listeners.get(event);
      if (!set) return;
      for (const l of set) l(payload);
    },
    removeAll() {
      listeners.clear();
    }
  };
}

// src/log.ts
var ORDER = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4
};
function defaultSink(entry) {
  if (typeof process !== "undefined" && process.stderr) {
    process.stderr.write(JSON.stringify(entry) + "\n");
  } else if (typeof console !== "undefined") {
    console.log(JSON.stringify(entry));
  }
}
function pushRing(ring, entry) {
  if (ring.capacity <= 0) return;
  ring.buffer.push(entry);
  if (ring.buffer.length > ring.capacity) ring.buffer.shift();
}
function buildLogger(level, ring, sink, bindings) {
  const threshold = ORDER[level];
  function emit(entryLevel, event, fields) {
    if (ORDER[entryLevel] < threshold) return;
    const entry = {
      ts: Date.now(),
      level: entryLevel,
      event,
      ...bindings,
      ...fields ?? {}
    };
    sink(entry);
    pushRing(ring, entry);
  }
  return {
    debug: (e, f) => emit("debug", e, f),
    info: (e, f) => emit("info", e, f),
    warn: (e, f) => emit("warn", e, f),
    error: (e, f) => emit("error", e, f),
    snapshot: () => [...ring.buffer],
    child: (extra) => buildLogger(level, ring, sink, { ...bindings, ...extra })
  };
}
function createLogger(opts = {}) {
  const level = opts.level ?? "info";
  const ring = {
    capacity: opts.ringCapacity ?? 0,
    buffer: []
  };
  const sink = opts.sink ?? defaultSink;
  return buildLogger(level, ring, sink, {});
}

// src/errors.ts
var VoipError = class extends Error {
  code;
  cause;
  constructor(code, message, cause) {
    super(message);
    this.name = "VoipError";
    this.code = code;
    if (cause !== void 0) this.cause = cause;
  }
};
var RegistrationError = class extends VoipError {
  constructor(message, cause) {
    super("REGISTRATION_FAILED", message, cause);
    this.name = "RegistrationError";
  }
};
var CallError = class extends VoipError {
  constructor(code, message, cause) {
    super(code, message, cause);
    this.name = "CallError";
  }
};

// src/sip/ua.ts
function unwrapJsSIP(mod) {
  if (mod && typeof mod === "object" && "UA" in mod && mod.UA) return mod;
  if (mod && typeof mod === "object" && "default" in mod) {
    const d = mod.default;
    if (d && "UA" in d) return d;
  }
  throw new VoipError("UNKNOWN", "Failed to load JsSIP \u2014 UA constructor not found");
}
async function createSipUA(opts) {
  const { config, logger } = opts;
  const connectTimeoutMs = opts.connectTimeoutMs ?? 3e3;
  const reconnectMinIntervalSec = opts.reconnectMinIntervalSec ?? 2;
  const reconnectMaxIntervalSec = opts.reconnectMaxIntervalSec ?? 30;
  const raw = await import('jssip');
  const JsSIP = unwrapJsSIP(raw);
  if (JsSIP.debug?.disable) JsSIP.debug.disable();
  if (!JsSIP.WebSocketInterface || !JsSIP.UA) {
    throw new VoipError("UNKNOWN", "JsSIP module missing UA / WebSocketInterface");
  }
  const socket = new JsSIP.WebSocketInterface(config.wsServer);
  const aor = `sip:${config.user}@${config.domain}`;
  const ua = new JsSIP.UA({
    uri: aor,
    password: config.password,
    display_name: config.displayName,
    sockets: [socket],
    register: false,
    register_expires: config.registerExpires ?? 600,
    user_agent: "pickit-voip/0.0.0",
    // RFC 4028 session timers — both peers send periodic re-INVITEs
    // (or UPDATEs) to confirm the call is still alive. Without this,
    // a half-collapsed transport drop can leave Asterisk thinking the
    // call is up while the browser has lost its RTCSession. The
    // session-timer refresh fails fast and the call ends cleanly.
    session_timers: true,
    connection_recovery_min_interval: reconnectMinIntervalSec,
    connection_recovery_max_interval: reconnectMaxIntervalSec
  });
  let connected = false;
  let registered = false;
  let wantsRegistered = false;
  const handlers = /* @__PURE__ */ new Set();
  function emit(ev) {
    for (const h of handlers) h(ev);
  }
  ua.on("connected", () => {
    connected = true;
    logger.info("sip.transport.connected");
    emit({ type: "transport-connected" });
    if (wantsRegistered && !registered) {
      try {
        ua.register();
      } catch (e) {
        logger.warn("sip.reconnect.register.threw", { error: e.message });
      }
    }
  });
  ua.on("disconnected", (raw2) => {
    connected = false;
    registered = false;
    const r = raw2;
    logger.info("sip.transport.disconnected", { code: r?.code, reason: r?.reason });
    emit({ type: "transport-disconnected", code: r?.code, reason: r?.reason });
  });
  ua.on("registered", () => {
    registered = true;
    logger.info("sip.registered", { aor });
    emit({ type: "registered" });
  });
  ua.on("unregistered", () => {
    registered = false;
    logger.info("sip.unregistered", { aor });
    emit({ type: "unregistered" });
  });
  ua.on("registrationFailed", (raw2) => {
    registered = false;
    const r = raw2;
    const cause = r?.cause ?? r?.response?.reason_phrase ?? "unknown";
    logger.warn("sip.registration.failed", {
      cause,
      status: r?.response?.status_code
    });
    emit({ type: "registration-failed", cause });
  });
  ua.on("newRTCSession", (raw2) => {
    const r = raw2;
    if (!r || r.originator !== "remote" || !r.session) return;
    const from = r.session.remote_identity?.uri?.toString?.() ?? "unknown";
    const rawDisplay = r.session.remote_identity?.display_name;
    const displayName = typeof rawDisplay === "string" && rawDisplay.length > 0 ? rawDisplay : null;
    const sess = r.session;
    const callId = sess.id ?? sess.request?.getHeader?.("Call-ID") ?? sess.request?.call_id ?? "";
    logger.info("sip.incoming-call", { from, displayName, callId });
    emit({ type: "incoming-call", session: r.session, from, displayName, callId });
  });
  return {
    async start() {
      if (connected) return;
      await new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          off();
          try {
            ua.stop();
          } catch {
          }
          reject(
            new VoipError(
              "WS_CONNECT_FAILED",
              `transport did not come up within ${connectTimeoutMs}ms`
            )
          );
        }, connectTimeoutMs);
        const off = subscribe(handlers, (ev) => {
          if (ev.type === "transport-connected") {
            clearTimeout(timer);
            off();
            resolve();
          } else if (ev.type === "transport-disconnected") {
            clearTimeout(timer);
            off();
            reject(new VoipError("WS_CONNECT_FAILED", `transport closed: ${ev.reason ?? "unknown"}`));
          }
        });
        ua.start();
      });
    },
    async stop() {
      wantsRegistered = false;
      if (!connected && !registered) {
        try {
          ua.stop();
        } catch {
        }
        return;
      }
      await new Promise((resolve) => {
        const off = subscribe(handlers, (ev) => {
          if (ev.type === "transport-disconnected") {
            off();
            resolve();
          }
        });
        try {
          ua.stop();
        } catch {
          off();
          resolve();
        }
      });
    },
    async register() {
      wantsRegistered = true;
      if (registered) return;
      await new Promise((resolve, reject) => {
        const off = subscribe(handlers, (ev) => {
          if (ev.type === "registered") {
            off();
            resolve();
          } else if (ev.type === "registration-failed") {
            off();
            reject(new RegistrationError(`registration failed: ${ev.cause}`));
          } else if (ev.type === "transport-disconnected") {
            off();
            reject(new RegistrationError(`transport closed during register: ${ev.reason ?? "unknown"}`));
          }
        });
        try {
          ua.register();
        } catch (e) {
          off();
          reject(new RegistrationError("ua.register() threw", e));
        }
      });
    },
    async unregister() {
      wantsRegistered = false;
      if (!registered) return;
      await new Promise((resolve) => {
        const off = subscribe(handlers, (ev) => {
          if (ev.type === "unregistered") {
            off();
            resolve();
          } else if (ev.type === "transport-disconnected") {
            off();
            resolve();
          }
        });
        try {
          ua.unregister({ all: true });
        } catch {
          off();
          resolve();
        }
      });
    },
    isConnected: () => connected,
    isRegistered: () => registered,
    on(handler) {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
    call(target, options) {
      const opts2 = {
        mediaStream: options.mediaStream,
        mediaConstraints: options.mediaConstraints ?? { audio: true, video: false }
      };
      if (options.iceServers) {
        opts2.pcConfig = { iceServers: options.iceServers };
      }
      return ua.call(target, opts2);
    }
  };
}
function subscribe(set, fn) {
  set.add(fn);
  return () => set.delete(fn);
}

// src/call.ts
var Call = class {
  id;
  direction;
  remoteIdentity;
  displayName;
  /** Clé de corrélation avec le CDR côté serveur (plan 04/T4) : pour un ENTRANT, le header
   *  `X-CX-Call-Id` posé par le dialplan porte le Call-ID du LEG APPELANT (celui que le
   *  userfield CDR enregistre) ; à défaut, le Call-ID de notre propre leg (exact pour un
   *  SORTANT — from-browser enregistre ce même id dans le userfield). */
  get sipCallId() {
    const req = this.session.request;
    const hdr = req?.getHeader?.("X-CX-Call-Id");
    if (hdr && hdr.trim()) return hdr.trim();
    return req?.call_id ?? req?.getHeader?.("Call-ID") ?? null;
  }
  session;
  log;
  emitter = createEmitter();
  _state = "initial";
  _endReason = null;
  _remoteTrack = null;
  qualityIntervalMs;
  qualityTimer = null;
  latestQuality = null;
  prevInbound = null;
  constructor(opts) {
    this.id = opts.id;
    this.direction = opts.direction;
    this.remoteIdentity = opts.remoteIdentity;
    this.displayName = opts.displayName;
    this.session = opts.session;
    this.log = opts.logger.child({ mod: "call", callId: this.id });
    this.qualityIntervalMs = opts.qualityIntervalMs ?? 5e3;
    this.wireSession();
  }
  getQuality() {
    return this.latestQuality;
  }
  get state() {
    return this._state;
  }
  get endReason() {
    return this._endReason;
  }
  get peerConnection() {
    return this.session.connection ?? null;
  }
  get remoteTrack() {
    return this._remoteTrack;
  }
  on(event, listener) {
    return this.emitter.on(event, listener);
  }
  off(event, listener) {
    this.emitter.off(event, listener);
  }
  hangup() {
    if (this._state === "ended") return;
    try {
      this.session.terminate?.();
    } catch {
    }
  }
  /** Accept an inbound call. Hands JsSIP a synthetic audio MediaStream. */
  accept(opts) {
    if (this.direction !== "inbound") {
      throw new Error("Call.accept() is only valid for inbound calls");
    }
    if (this._state === "ended") {
      throw new Error("Call.accept() called on an already-ended call");
    }
    try {
      const sipOpts = {
        mediaStream: opts.mediaStream,
        mediaConstraints: { audio: true, video: false }
      };
      if (opts.iceServers) sipOpts.pcConfig = { iceServers: opts.iceServers };
      this.session.answer?.(sipOpts);
    } catch (e) {
      this.log.error("call.accept.threw", { error: e.message });
      throw e;
    }
  }
  /** Toggle local audio mute. Disables the outbound track without re-INVITE. */
  mute(muted) {
    if (muted) {
      this.session.mute?.({ audio: true });
    } else {
      this.session.unmute?.({ audio: true });
    }
  }
  isMuted() {
    const m = this.session.isMuted?.();
    return !!m?.audio;
  }
  /** Put the call on hold. Sends a re-INVITE with `a=sendonly`. */
  async hold() {
    if (this._state === "ended") throw new Error("Call.hold() on ended call");
    await new Promise((resolve, reject) => {
      try {
        this.session.hold?.(void 0, () => {
          this.setState("on-hold");
          resolve();
        });
        setTimeout(() => {
          this.setState("on-hold");
          resolve();
        }, 1500);
      } catch (e) {
        reject(e);
      }
    });
  }
  async resume() {
    if (this._state === "ended") throw new Error("Call.resume() on ended call");
    await new Promise((resolve, reject) => {
      try {
        this.session.unhold?.(void 0, () => {
          this.setState("connected");
          resolve();
        });
        setTimeout(() => {
          if (this._state === "on-hold") this.setState("connected");
          resolve();
        }, 1500);
      } catch (e) {
        reject(e);
      }
    });
  }
  isOnHold() {
    const h = this.session.isOnHold?.();
    return !!h?.local;
  }
  /** Send DTMF tones. Default transport is SIP INFO (easiest for tests/mocks). */
  sendDtmf(tones, opts = {}) {
    if (this._state === "ended") throw new Error("Call.sendDtmf() on ended call");
    this.session.sendDTMF?.(tones, {
      duration: opts.duration ?? 100,
      interToneGap: opts.interToneGap ?? 70,
      transportType: opts.transport ?? "INFO"
    });
  }
  /**
   * Blind transfer the active call to `target`. The SDK fires SIP
   * REFER; Asterisk runs the new dialplan, opens a bridge to the
   * target, and SHOULD send us a BYE — but in practice the BYE is
   * delayed (or sometimes never arrives, depending on which side
   * answers first), and the UI sits with a phantom "active call".
   *
   * To avoid that, we listen on the JsSIP referSubscriber: as soon as
   * it accepts (the dialog with the transferee is established —
   * Asterisk replied 202 Accepted to our REFER and is now handling
   * the rest server-side), we proactively hang up our leg. The host
   * agent's call is ended cleanly and immediately. If the REFER
   * fails, we leave the call alone so the agent can retry.
   */
  transfer(target) {
    if (this._state === "ended") throw new Error("Call.transfer() on ended call");
    if (!this.session.refer) throw new Error("transfer not supported by this transport");
    const sub = this.session.refer(target);
    if (!sub || typeof sub.on !== "function") return;
    sub.on("accepted", () => {
      this.log.info("call.transfer.accepted; hanging up local leg");
      try {
        this.session.terminate?.();
      } catch {
      }
    });
    sub.on("failed", (raw) => {
      const r = raw;
      this.log.warn("call.transfer.failed", { cause: r?.cause });
    });
  }
  /** Reject an inbound call with the given SIP status (default 486 Busy Here). */
  reject(opts = {}) {
    if (this.direction !== "inbound") {
      throw new Error("Call.reject() is only valid for inbound calls");
    }
    try {
      this.session.terminate?.({
        status_code: opts.status ?? 486,
        reason_phrase: opts.reason ?? "Busy Here"
      });
    } catch {
    }
  }
  /** Resolve when the call enters `connected`. Rejects if it ends first. */
  waitConnected(timeoutMs = 15e3) {
    if (this._state === "connected") return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        offConn();
        offEnd();
        reject(new Error(`call did not connect within ${timeoutMs}ms`));
      }, timeoutMs);
      const offConn = this.on("connected", () => {
        clearTimeout(timer);
        offConn();
        offEnd();
        resolve();
      });
      const offEnd = this.on("ended", (e) => {
        clearTimeout(timer);
        offConn();
        offEnd();
        reject(new Error(`call ended before connecting: ${e.reason}`));
      });
    });
  }
  wireSession() {
    this.session.on("progress", () => {
      this.log.info("call.progress");
      this.setState("ringing");
      this.emitter.emit("ringing", void 0);
    });
    this.session.on("accepted", () => {
      this.log.info("call.accepted");
      this.setState("connecting");
    });
    this.session.on("confirmed", () => {
      this.log.info("call.confirmed");
      this.setState("connected");
      this.captureRemoteTrack();
      this.startQualityPolling();
      this.emitter.emit("connected", void 0);
    });
    this.session.on("ended", (raw) => {
      const r = raw;
      const reason = mapEndCause(r?.cause);
      this.log.info("call.ended", { cause: r?.cause, reason });
      this._endReason = reason;
      this.stopQualityPolling();
      this.setState("ended");
      this.emitter.emit("ended", { reason });
    });
    this.session.on("failed", (raw) => {
      const r = raw;
      const reason = mapEndCause(r?.cause);
      this.log.warn("call.failed", { cause: r?.cause, reason });
      this._endReason = reason;
      this.stopQualityPolling();
      this.setState("ended");
      this.emitter.emit("ended", { reason });
    });
    this.session.on("peerconnection", (raw) => {
      const ev = raw;
      const pc = ev?.peerconnection;
      if (!pc) return;
      pc.addEventListener("track", (te) => {
        const ev2 = te;
        const t = ev2.track;
        if (t && t.kind === "audio" && !this._remoteTrack) {
          this._remoteTrack = t;
          this.emitter.emit("remote-track", t);
        }
      });
    });
  }
  captureRemoteTrack() {
    if (this._remoteTrack) return;
    const pc = this.session.connection;
    if (!pc) return;
    const receivers = pc.getReceivers?.() ?? [];
    for (const r of receivers) {
      const t = r.track;
      if (t && t.kind === "audio") {
        this._remoteTrack = t;
        this.emitter.emit("remote-track", t);
        return;
      }
    }
  }
  setState(next) {
    if (this._state === next) return;
    this._state = next;
    this.emitter.emit("state-changed", next);
  }
  startQualityPolling() {
    if (this.qualityIntervalMs <= 0 || this.qualityTimer) return;
    const tick = () => {
      void this.sampleQuality();
    };
    setTimeout(tick, Math.min(250, this.qualityIntervalMs));
    this.qualityTimer = setInterval(tick, this.qualityIntervalMs);
  }
  stopQualityPolling() {
    if (!this.qualityTimer) return;
    clearInterval(this.qualityTimer);
    this.qualityTimer = null;
  }
  async sampleQuality() {
    const pc = this.session.connection;
    if (!pc) return;
    let report;
    try {
      report = await pc.getStats();
    } catch (e) {
      this.log.warn("quality.getStats.threw", { error: e.message });
      return;
    }
    const m = computeMetrics(report, this.prevInbound);
    if (!m) return;
    this.latestQuality = m.metrics;
    this.prevInbound = m.nextPrev;
    this.emitter.emit("quality", m.metrics);
  }
};
function computeMetrics(report, prev) {
  let inbound = null;
  let candidatePair = null;
  let remoteInbound = null;
  report.forEach((raw) => {
    const r = raw;
    const t = r["type"];
    const kind = r["kind"] ?? r["mediaType"];
    if (t === "inbound-rtp" && (kind === "audio" || kind === void 0)) inbound = r;
    if (t === "remote-inbound-rtp" && (kind === "audio" || kind === void 0)) remoteInbound = r;
    if (t === "candidate-pair" && r["state"] === "succeeded" && (r["nominated"] === true || candidatePair == null)) {
      candidatePair = r;
    }
  });
  if (!inbound) return null;
  const inb = inbound;
  const cand = candidatePair;
  const rin = remoteInbound;
  const jitterMs = (inb["jitter"] ?? 0) * 1e3;
  const packetsReceived = inb["packetsReceived"] ?? 0;
  const packetsLost = inb["packetsLost"] ?? 0;
  const total = packetsReceived + packetsLost;
  const packetLossPct = total > 0 ? packetsLost / total * 100 : 0;
  const rttSec = cand?.["currentRoundTripTime"] ?? rin?.["roundTripTime"] ?? 0;
  const rttMs = rttSec * 1e3;
  const bytes = inb["bytesReceived"] ?? 0;
  const tsRaw = inb["timestamp"];
  const ts = typeof tsRaw === "number" ? tsRaw : Date.now();
  let bitrateKbps = 0;
  if (prev && ts > prev.ts) {
    const dtSec = (ts - prev.ts) / 1e3;
    if (dtSec > 0) bitrateKbps = (bytes - prev.bytes) * 8 / 1e3 / dtSec;
  }
  const mosScore = estimateMos(jitterMs, packetLossPct, rttMs);
  return {
    metrics: { timestamp: ts, jitterMs, packetLossPct, rttMs, bitrateKbps, mosScore },
    nextPrev: { bytes, ts }
  };
}
function estimateMos(jitterMs, lossPct, rttMs) {
  let mos = 4.5;
  mos -= Math.min(lossPct, 30) * 0.2;
  mos -= Math.min(jitterMs, 200) / 100;
  mos -= Math.max(0, rttMs - 150) / 200;
  return Math.max(1, Math.min(5, Number(mos.toFixed(2))));
}
function mapEndCause(cause) {
  if (!cause) return "unknown";
  const c = cause.toLowerCase();
  if (c.includes("user denied")) return "rejected";
  if (c.includes("rejected")) return "rejected";
  if (c.includes("busy")) return "busy";
  if (c.includes("no answer") || c.includes("request timeout")) return "no-answer";
  if (c.includes("canceled")) return "cancelled";
  if (c.includes("bye")) return "remote-hangup";
  if (c.includes("terminated")) return "local-hangup";
  if (c.includes("connection error")) return "network-error";
  if (c.includes("unavailable")) return "no-answer";
  return "unknown";
}

// src/platform/index.ts
var _override = null;
function setPlatform(p) {
  _override = p;
}
function getPlatform() {
  if (_override) return _override;
  if (typeof globalThis !== "undefined" && typeof globalThis["WebSocket"] === "function") {
    throw new Error(
      "Platform not initialised. Call `loadBrowserPlatform()` (browser) or `await loadNodePlatform()` (node) before using VoipClient."
    );
  }
  throw new Error(
    "No platform installed. In Node call `await loadNodePlatform()`; in the browser call `loadBrowserPlatform()` once at startup."
  );
}

// src/voip-client.ts
var VoipClient = class {
  config;
  log;
  emitter = createEmitter();
  ua = null;
  uaUnsubscribe = null;
  uaInjected = null;
  state = "idle";
  /** True if the consumer called disconnect() — suppresses reconnect logic. */
  userClosed = false;
  /** Counters surfaced via getReconnectStats() and useful in tests. */
  reconnectStats = { disconnects: 0, recoveries: 0 };
  constructor(config, internal = {}) {
    this.config = validateConfig(config);
    void internal._clock;
    this.log = internal._logger ?? createLogger({ level: config.logLevel ?? "info" }).child({ mod: "voip" });
    this.uaInjected = internal._ua ?? null;
  }
  getReconnectStats() {
    return { ...this.reconnectStats };
  }
  on(event, listener) {
    return this.emitter.on(event, listener);
  }
  off(event, listener) {
    this.emitter.off(event, listener);
  }
  getState() {
    return this.state;
  }
  getConfig() {
    return this.config;
  }
  /**
   * Resolve the ICE servers to use for the next call. Prefers the
   * `iceServersProvider` (fresh TURN credential) and falls back to the
   * static `iceServers` if the provider is absent, throws, or returns
   * nothing. Never throws.
   */
  async resolveIceServers() {
    if (this.config.iceServersProvider) {
      try {
        const fresh = await this.config.iceServersProvider();
        if (fresh && fresh.length > 0) return fresh;
        this.log.warn("iceServers.provider.empty", { fallback: "static" });
      } catch (e) {
        this.log.warn("iceServers.provider.failed", {
          error: e instanceof Error ? e.message : String(e),
          fallback: "static"
        });
      }
    }
    return this.config.iceServers;
  }
  /** Open the WSS transport. Does not register. */
  async connect() {
    if (this.state === "connected" || this.state === "registered") {
      this.log.warn("connect.skip", { state: this.state });
      return;
    }
    this.userClosed = false;
    if (!this.ua) {
      this.ua = this.uaInjected ?? await createSipUA({
        config: this.config.sip,
        logger: this.log,
        // JsSIP requires connection_recovery_min_interval to be an integer
        // ≥ 1 second. We pin sub-second backoffs at 1.
        ...this.config.reconnect?.backoffMs !== void 0 ? { reconnectMinIntervalSec: Math.max(1, Math.round(this.config.reconnect.backoffMs / 1e3)) } : {}
      });
      this.uaUnsubscribe = this.ua.on((ev) => this.onUAEvent(ev));
    }
    this.setState("connecting");
    try {
      await this.ua.start();
      this.setState("connected");
      this.emitter.emit("connected", void 0);
    } catch (e) {
      this.setState("failed");
      const err = e instanceof VoipError ? e : new VoipError("WS_CONNECT_FAILED", String(e), e);
      this.emitter.emit("error", err);
      throw err;
    }
  }
  async disconnect() {
    this.userClosed = true;
    if (!this.ua) {
      this.setState("disconnected");
      return;
    }
    try {
      await this.ua.stop();
    } finally {
      this.uaUnsubscribe?.();
      this.uaUnsubscribe = null;
      this.ua = null;
      this.setState("disconnected");
      this.emitter.emit("disconnected", { code: 1e3, reason: "client disconnect" });
    }
  }
  async register() {
    if (!this.ua || this.state === "idle" || this.state === "disconnected" || this.state === "failed") {
      throw new VoipError("WS_DISCONNECTED", "VoipClient.register() requires connect() first");
    }
    if (this.state === "registered") {
      this.log.warn("register.skip", { state: this.state });
      return;
    }
    try {
      await this.ua.register();
      this.setState("registered");
      this.emitter.emit("registered", void 0);
    } catch (e) {
      const err = e instanceof RegistrationError ? e : new RegistrationError(e instanceof Error ? e.message : String(e), e);
      this.emitter.emit("registration-failed", err);
      throw err;
    }
  }
  async unregister() {
    if (!this.ua || this.state !== "registered") {
      this.log.warn("unregister.skip", { state: this.state });
      return;
    }
    await this.ua.unregister();
    this.setState("connected");
    this.emitter.emit("unregistered", void 0);
  }
  isRegistered() {
    return this.state === "registered";
  }
  // ── Audio devices ──────────────────────────────────────────────
  async getAudioDevices() {
    return getPlatform().getAudioDevices();
  }
  async setInputDevice(deviceId) {
    return getPlatform().setInputDevice(deviceId);
  }
  async setOutputDevice(deviceId) {
    return getPlatform().setOutputDevice(deviceId);
  }
  /**
   * Place an outbound call. The returned Call resolves on completion via
   * `call.waitConnected()` or by listening to its events.
   *
   * `options.mediaStream` lets the consumer pass a real MediaStream
   * (e.g. `await navigator.mediaDevices.getUserMedia({audio:true})`).
   * If omitted, the platform's synthetic stream is used — that's fine
   * for tests and headless smokes but means the *browser never asks for
   * mic permission*, so nothing the user says reaches the called party.
   */
  /** Le dernier appel SORTANT non terminé — un 2e dial() est refusé tant qu'il vit
   *  (défense en profondeur du plan 03-S3 : aucun consommateur ne peut recréer le
   *  double-INVITE, même sans le verrou du widget). */
  outboundInFlight = null;
  async dial(target, options = {}) {
    if (!this.ua) {
      throw new VoipError("WS_DISCONNECTED", "VoipClient.dial() requires connect() first");
    }
    if (!this.ua.isConnected()) {
      throw new VoipError(
        "WS_DISCONNECTED",
        "transport SIP indisponible \u2014 reconnexion en cours, r\xE9essayez dans quelques secondes"
      );
    }
    if (this.outboundInFlight && this.outboundInFlight.state !== "ended") {
      throw new CallError("CALL_IN_PROGRESS", "un appel sortant est d\xE9j\xE0 en cours d'initiation ou actif");
    }
    const platform = getPlatform();
    const explicitStream = options.mediaStream;
    const synth = explicitStream ? null : platform.createSyntheticAudioStream();
    const stream = explicitStream ?? synth.stream;
    const sip = this.config.sip;
    const sipTarget = target.startsWith("sip:") ? target : `sip:${target}@${sip.domain}`;
    const callId = `call-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    this.log.info("dial.start", { target: sipTarget });
    const iceServers = await this.resolveIceServers();
    let session;
    try {
      const callOpts = {
        mediaStream: stream
      };
      if (iceServers) callOpts.iceServers = iceServers;
      session = this.ua.call(sipTarget, callOpts);
    } catch (e) {
      synth?.stop();
      throw new CallError("CALL_FAILED", `ua.call() threw: ${e instanceof Error ? e.message : String(e)}`, e);
    }
    const call = new Call({
      id: callId,
      direction: "outbound",
      remoteIdentity: sipTarget,
      // Outbound calls don't receive a display name from the peer at
      // dial-time; null is fine and consumers can fall back to the URI.
      displayName: null,
      session,
      logger: this.log,
      qualityIntervalMs: this.config.qualityIntervalMs ?? 5e3
    });
    const stopSine = synth ? synth.startSineWave({ hz: 440, gain: 0.5 }) : null;
    this.outboundInFlight = call;
    call.on("ended", () => {
      if (this.outboundInFlight === call) this.outboundInFlight = null;
      stopSine?.();
      synth?.stop();
    });
    return call;
  }
  onUAEvent(ev) {
    switch (ev.type) {
      case "transport-disconnected":
        if (this.userClosed) return;
        this.reconnectStats.disconnects += 1;
        if (this.state !== "reconnecting" && this.state !== "disconnected" && this.state !== "failed") {
          this.setState("reconnecting");
          this.emitter.emit("disconnected", { code: ev.code, reason: ev.reason });
        }
        return;
      case "transport-connected":
        if (this.state === "reconnecting") {
          this.setState("connecting");
        }
        return;
      case "registered":
        if (this.state !== "registered") {
          if (this.reconnectStats.disconnects > 0) this.reconnectStats.recoveries += 1;
          this.setState("registered");
          this.emitter.emit("registered", void 0);
        }
        return;
      case "registration-failed": {
        const err = new RegistrationError(`registration failed: ${ev.cause}`);
        this.emitter.emit("registration-failed", err);
        return;
      }
      case "unregistered":
        if (this.state === "registered") {
          this.setState("connected");
          this.emitter.emit("unregistered", void 0);
        }
        return;
      case "incoming-call": {
        const call = new Call({
          id: ev.callId,
          direction: "inbound",
          remoteIdentity: ev.from,
          displayName: ev.displayName,
          session: ev.session,
          logger: this.log,
          qualityIntervalMs: this.config.qualityIntervalMs ?? 5e3
        });
        this.log.info("incoming-call", {
          from: ev.from,
          displayName: ev.displayName,
          callId: ev.callId
        });
        this.emitter.emit("incoming-call", call);
        return;
      }
      // transport-connected and registered are handled via the awaited
      // promises in connect() / register(); avoid double-emitting here.
      default:
        return;
    }
  }
  setState(next) {
    if (this.state === next) return;
    this.state = next;
    this.emitter.emit("connection-state", next);
  }
};
function validateConfig(config) {
  if (!config || typeof config !== "object") {
    throw new VoipError("CONFIG_INVALID", "VoipConfig must be an object");
  }
  const { sip } = config;
  if (!sip || typeof sip !== "object") {
    throw new VoipError("CONFIG_INVALID", "VoipConfig.sip is required");
  }
  for (const key of ["wsServer", "user", "password", "domain"]) {
    if (typeof sip[key] !== "string" || sip[key].length === 0) {
      throw new VoipError("CONFIG_INVALID", `VoipConfig.sip.${key} is required`);
    }
  }
  if (!/^wss?:\/\//.test(sip.wsServer)) {
    throw new VoipError(
      "CONFIG_INVALID",
      `VoipConfig.sip.wsServer must start with ws:// or wss:// \u2014 got "${sip.wsServer}"`
    );
  }
  return config;
}

// src/platform/browser.ts
function loadBrowserPlatform() {
  if (typeof globalThis === "undefined" || typeof globalThis["WebSocket"] !== "function") {
    throw new Error("loadBrowserPlatform called in a non-browser environment");
  }
  const g = globalThis;
  const platform = {
    kind: "browser",
    WebSocket: g["WebSocket"],
    RTCPeerConnection: g["RTCPeerConnection"],
    MediaStream: g["MediaStream"],
    createSyntheticAudioStream() {
      const Ctx = g["AudioContext"] ?? g["webkitAudioContext"];
      const ctx = new Ctx();
      const dest = ctx.createMediaStreamDestination();
      const osc = ctx.createOscillator();
      osc.frequency.value = 440;
      osc.connect(dest);
      osc.start();
      const track = dest.stream.getAudioTracks()[0];
      return {
        stream: dest.stream,
        track,
        push: (_frame) => {
        },
        startSineWave: (_o) => {
          return () => {
          };
        },
        stop: () => {
          try {
            osc.stop();
            track.stop();
            void ctx.close();
          } catch {
          }
        }
      };
    },
    attachAudioSink(_track) {
      return {
        stop() {
        }
      };
    },
    async getAudioDevices() {
      const list = await navigator.mediaDevices.enumerateDevices();
      return {
        inputs: list.filter((d) => d.kind === "audioinput"),
        outputs: list.filter((d) => d.kind === "audiooutput"),
        selectedInput: null,
        selectedOutput: null
      };
    },
    async setInputDevice(_deviceId) {
    },
    async setOutputDevice(_deviceId) {
    }
  };
  setPlatform(platform);
  return platform;
}

export { Call, CallError, RegistrationError, VoipClient, VoipError, createLogger, loadBrowserPlatform };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map